import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loader2, Trash2, Mail, Phone, MessageCircle, BookOpen } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

export default function ChatLeadsInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { toast } = useToast();

  const fetch = () => {
    setLoading(true);
    api.get("/chat-leads").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(fetch, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/chat-leads/${id}`);
    toast({ title: "Deleted" });
    if (selected?.id === id) setSelected(null);
    fetch();
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#0B2A6B]">Chat Assistant Leads</h2>
        <p className="text-sm text-slate-500">Leads captured via the AI chat assistant.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-slate-500" />
            <span className="font-semibold text-sm text-slate-700">Leads</span>
            <span className="ml-auto text-xs text-slate-400">{items.length}</span>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center text-slate-400 text-sm">No leads yet</div>
            ) : items.map((it) => (
              <button
                key={it.id}
                onClick={() => setSelected(it)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-yellow-50 transition ${
                  selected?.id === it.id ? "bg-yellow-50" : ""
                }`}
              >
                <div className="flex justify-between items-baseline">
                  <span className="font-semibold text-[#0B2A6B] text-sm">{it.name}</span>
                  <span className="text-[11px] text-slate-400">{new Date(it.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1 truncate">{it.course_interest}</div>
                <div className="text-xs text-slate-400 mt-0.5">{it.phone}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          {selected ? (
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-[#0B2A6B]">{selected.name}</h3>
                  <p className="text-xs text-slate-400 mt-1">{new Date(selected.created_at).toLocaleString()}</p>
                </div>
                <button onClick={() => remove(selected.id)} className="px-3 py-1.5 text-xs rounded-lg bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mb-5 text-sm">
                <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-yellow-600">
                  <Phone className="h-4 w-4" />{selected.phone}
                </a>
                {selected.email && (
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-slate-700 hover:text-yellow-600 truncate">
                    <Mail className="h-4 w-4 shrink-0" /><span className="truncate">{selected.email}</span>
                  </a>
                )}
                {selected.course_interest && (
                  <div className="flex items-center gap-2 text-slate-700 sm:col-span-2">
                    <BookOpen className="h-4 w-4" />Interested in: <strong>{selected.course_interest}</strong>
                  </div>
                )}
              </div>
              {selected.initial_message && (
                <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3 text-sm text-slate-700 mb-4">
                  <strong className="text-yellow-700 text-xs uppercase block mb-1">Initial Message</strong>
                  {selected.initial_message}
                </div>
              )}
              {selected.transcript?.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-slate-700 mb-2">Conversation</h4>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2 text-sm max-h-80 overflow-y-auto">
                    {selected.transcript.map((m, i) => (
                      <div key={i} className={`flex ${m.from === "user" ? "justify-end" : ""}`}>
                        <div className={`px-3 py-1.5 rounded-lg max-w-[80%] ${
                          m.from === "user" ? "bg-[#0B2A6B] text-white" : "bg-white border border-slate-200"
                        }`}>
                          {m.text}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-20 text-center text-slate-400">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
              Select a lead to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
